import React, { createContext, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';

const UserContext = createContext(null);

export const USER_QUERY_KEY = ['admin-user', 'me'];

/**
 * Fetches /admin-users/me. Returns the whole response payload
 * (user, ipChecked, mfaVerified). Consumers should prefer `useCurrentUser`
 * from the provider below for cached access instead of calling this directly.
 */
const useUserQuery = () =>
    useQuery({
        queryKey: USER_QUERY_KEY,
        queryFn: authApi.me,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false
    });

export const UserProvider = ({ children }) => {
    const { data, isLoading, isError, refetch } = useUserQuery();

    const value = useMemo(() => {
        const user = data?.user || null;
        const accesses = user?.accesses || [];
        return {
            user,
            accesses,
            ipChecked: !!data?.ipChecked,
            mfaVerified: !!data?.mfaVerified,
            mfaSecretExists: !!user?.mfaSecretExists,
            isLoading,
            isError,
            refetch
        };
    }, [data, isLoading, isError, refetch]);

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useCurrentUser = () => {
    const ctx = useContext(UserContext);
    if (!ctx) throw new Error('useCurrentUser must be used inside <UserProvider>');
    return ctx;
};

export const useInvalidateCurrentUser = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
};

export const useSetCurrentUser = () => {
    const queryClient = useQueryClient();
    return (payload) => queryClient.setQueryData(USER_QUERY_KEY, payload);
};
